<template>
  <div class="category-management">
    <div class="toolbar">
      <el-button type="primary" @click="showAddDialog">添加分类</el-button>
    </div>

    <div class="filters">
      <el-input
        v-model="searchKeyword"
        placeholder="搜索分类名称或描述..."
        class="search-input"
        clearable
        @input="handleSearch"
        style="width: 300px;"
      >
        <template #prefix>
          <el-icon><Search /></el-icon>
        </template>
      </el-input>
    </div>

    <div class="sortable-list" v-loading="loading">
      <div
        v-for="(item, index) in displayItems"
        :key="item.id"
        class="sortable-item"
        :draggable="true"
        @dragstart="handleDragStart(index, $event)"
        @dragover.prevent="handleDragOver($event)"
        @drop="handleDrop(index, $event)"
        @dragend="handleDragEnd"
      >
        <div class="drag-handle">
          <el-icon><Rank /></el-icon>
        </div>
        <div class="item-content">
          <div class="item-name">{{ item.name }}</div>
          <div class="item-description">{{ item.description || '-' }}</div>
        </div>
        <div class="item-status">
          <el-switch
            v-model="item.is_active"
            @change="toggleActive(item)"
            active-text="启用"
            inactive-text="禁用"
          />
        </div>
        <div class="item-actions">
          <el-button size="small" @click="editItem(item)">编辑</el-button>
          <el-button size="small" type="danger" @click="deleteItem(item)">删除</el-button>
        </div>
      </div>
      <div v-if="displayItems.length === 0" class="empty-state">
        <el-empty description="暂无分类" />
      </div>
    </div>
    
    <div class="sort-tip" v-if="displayItems.length > 0">
      <el-icon><InfoFilled /></el-icon>
      <span>拖拽左侧图标可调整分类排序，排序将在保存后生效</span>
      <el-button size="small" type="primary" @click="saveOrder" :loading="savingOrder" style="margin-left: 15px;">
        保存排序
      </el-button>
    </div>

    <el-pagination
      v-if="total > 0"
      :current-page="currentPage"
      :page-sizes="[20, 50, 100, { value: -1, label: '全部' }]"
      :page-size="pageSize"
      :total="total"
      layout="total, sizes, prev, pager, next, jumper"
      @size-change="handleSizeChange"
      @current-change="handlePageChange"
      class="pagination"
    />

    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="600px">
      <el-form :model="form" label-width="100px">
        <el-form-item label="名称" required>
          <el-input v-model="form.name" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="form.description" type="textarea" :rows="4" />
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="form.is_active" active-text="启用" inactive-text="禁用" />
          <div class="form-tip">禁用后，该分类及其下的所有资源将不在前台显示</div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveItem">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import api from '../../api'
import { Search, Rank, InfoFilled } from '@element-plus/icons-vue'

export default {
  name: 'CategoryManagement',
  components: {
    Search,
    Rank,
    InfoFilled
  },
  data() {
    return {
      items: [],
      searchKeyword: '',
      currentPage: 1,
      pageSize: 20,
      total: 0,
      loading: false,
      dialogVisible: false,
      dialogTitle: '添加分类',
      form: {
        name: '',
        description: '',
        is_active: true
      },
      currentId: null,
      draggedIndex: null,
      savingOrder: false
    }
  },
  computed: {
    displayItems() {
      return this.items
    }
  },
  mounted() {
    this.loadItems()
  },
  methods: {
    async loadItems() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          page_size: this.pageSize === -1 ? 'all' : this.pageSize
        }
        
        // 添加搜索
        if (this.searchKeyword) {
          params.search = this.searchKeyword
        }
        
        const response = await api.get('/admin/market/categories/', { params })
        
        if (Array.isArray(response.data)) {
          this.items = response.data
          this.total = response.data.length
        } else {
          this.items = response.data.results || []
          this.total = response.data.count || 0
        }
      } catch (error) {
        this.$message.error('加载失败')
        this.items = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    showAddDialog() {
      this.dialogTitle = '添加分类'
      this.currentId = null
      this.form = {
        name: '',
        description: '',
        is_active: true
      }
      this.dialogVisible = true
    },
    editItem(item) {
      this.dialogTitle = '编辑分类'
      this.currentId = item.id
      this.form = {
        name: item.name,
        description: item.description || '',
        is_active: item.is_active !== undefined ? item.is_active : true
      }
      this.dialogVisible = true
    },
    async toggleActive(item) {
      try {
        await api.patch(`/admin/market/categories/${item.id}/`, { is_active: item.is_active })
        this.$message.success(item.is_active ? '已启用' : '已禁用')
      } catch (error) {
        // 恢复原状态
        item.is_active = !item.is_active
        this.$message.error('操作失败')
        console.error(error)
      }
    },
    async saveItem() {
      try {
        if (!this.form.name) {
          this.$message.warning('请输入名称')
          return
        }
        if (this.currentId) {
          await api.put(`/admin/market/categories/${this.currentId}/`, this.form)
          this.$message.success('更新成功')
        } else {
          await api.post('/admin/market/categories/', this.form)
          this.$message.success('添加成功')
        }
        this.dialogVisible = false
        this.loadItems()
      } catch (error) {
        this.$message.error('保存失败')
        console.error(error)
      }
    },
    async deleteItem(item) {
      try {
        await this.$confirm(`确定要删除分类"${item.name}"吗？`, '提示', {
          type: 'warning'
        })
        await api.delete(`/admin/market/categories/${item.id}/`)
        this.$message.success('删除成功')
        this.loadItems()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败')
          console.error(error)
        }
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    handleSearch() {
      this.currentPage = 1
      this.loadItems()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.loadItems()
    },
    handlePageChange(page) {
      this.currentPage = page
      if (this.pageSize !== -1) {
        this.loadItems()
      }
    },
    handleDragStart(index, event) {
      this.draggedIndex = index
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/html', event.target.outerHTML)
      event.target.style.opacity = '0.5'
    },
    handleDragOver(event) {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
    },
    handleDrop(dropIndex, event) {
      event.preventDefault()
      if (this.draggedIndex === null || this.draggedIndex === dropIndex) {
        return
      }
      
      const draggedItem = this.items[this.draggedIndex]
      this.items.splice(this.draggedIndex, 1)
      this.items.splice(dropIndex, 0, draggedItem)
      this.draggedIndex = null
    },
    handleDragEnd(event) {
      event.target.style.opacity = '1'
      this.draggedIndex = null
    },
    async saveOrder() {
      this.savingOrder = true
      try {
        const items = this.items.map((item, index) => ({
          id: item.id,
          sort_order: (this.items.length - index) * 10 // 数字越大越靠前
        }))
        
        await api.post('/admin/market/categories/update_order/', { items })
        this.$message.success('排序已保存')
        this.loadItems() // 重新加载以获取最新的排序
      } catch (error) {
        console.error('保存排序失败:', error)
        this.$message.error('保存排序失败')
      } finally {
        this.savingOrder = false
      }
    }
  }
}
</script>

<style scoped>
.category-management {
  color: #e0e0e0;
}

.toolbar {
  margin-bottom: 20px;
}

.filters {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.sortable-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sortable-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 18px 20px;
  background: linear-gradient(135deg, rgba(20, 20, 30, 0.95), rgba(25, 25, 35, 0.9));
  border: 1px solid rgba(0, 150, 255, 0.3);
  border-radius: 10px;
  cursor: move;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.sortable-item:hover {
  border-color: rgba(0, 212, 255, 0.6);
  background: linear-gradient(135deg, rgba(0, 150, 255, 0.15), rgba(0, 212, 255, 0.1));
  box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);
  transform: translateY(-2px);
}

.sortable-item.dragging {
  opacity: 0.6;
  transform: scale(1.02);
  box-shadow: 0 6px 20px rgba(0, 212, 255, 0.5);
}

.drag-handle {
  color: rgba(0, 212, 255, 0.8);
  cursor: grab;
  font-size: 22px;
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.drag-handle:hover {
  background: rgba(0, 212, 255, 0.2);
  color: #00d4ff;
  transform: scale(1.1);
}

.drag-handle:active {
  cursor: grabbing;
  transform: scale(0.95);
}

.item-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.item-name {
  font-size: 16px;
  font-weight: 600;
  color: #e0e0e0;
  margin-bottom: 5px;
}

.item-description {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.5;
}

.item-status {
  display: flex;
  align-items: center;
  margin-right: 15px;
}

.item-actions {
  display: flex;
  gap: 10px;
}

.sort-tip {
  margin-top: 20px;
  padding: 15px;
  background: rgba(0, 150, 255, 0.1);
  border: 1px solid rgba(0, 150, 255, 0.3);
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
}

.empty-state {
  padding: 40px;
  text-align: center;
}
</style>

